import { getQueryKey } from "@trpc/react-query";
import { RouterInputs, RouterOutputs, trpc } from "@utils/trpc";
import { useEffect } from "react";
import {
  InfiniteData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { InvalidOptionsError } from "@utils/InvalidOptionsError";
import { NoAuthError } from "@utils/NoAuthError";
import { Platform } from "react-native";
import { waitForAuthReady } from "@utils/auth/waitForAuthReady";
import { useData } from "../../runtime/provider";
import type { DataSession } from "../../data/core/session";
import { isAccessNetworkError } from "./accessCache";
import { infiniteDataSchema } from "@utils/durableCache/schemas";
import {
  accessTicketSchema,
  accessTicketsListSchema,
  getAccessByIdOutputSchema,
  type AccessById,
} from "@libs/wire";
import type { Purchase } from "@libs/platform-wire/purchase";
import { cachedAccesses } from "@sqlite/db";
import { z } from "zod";

type Accesses = RouterOutputs["accesses"]["listAll"];
/** One access as the app's lists read it: a headline, no purchase. */
type Access = import("zod").z.infer<typeof accessTicketSchema>;
/** One access as the app opens it (`accesses.getById`): an event or a
 *  merch product carries the purchase the sheet draws (LIS-1173). Only this
 *  read fills its cache, so its value always carries the purchase; a saved
 *  value without it fails its schema and is read again. */
type AccessRead = AccessById;
type WithoutPurchase<T> = T extends unknown ? Omit<T, "purchase"> : never;

/*
 * The access reads that price merch (`accesses.getById`, the wallet and an
 * artist's list) name no `country`: the server takes the fan's plan
 * country, else the device region the app sends as `country-code`. Nor
 * `deliverTo`, but the purchase sheet's own read: no address is known
 * while listing.
 */

/**
 * Bidirectional drift checks pinning the access wire schemas to the trpc
 * router output. Forward catches "schema is missing fields"; backward
 * catches "schema is narrower than router output" (which would reject
 * valid server data and pin the cache to stale rows). See
 * `useLibrary/queryFunctions.ts` for the full rationale.
 */
type _AccessWireSchemaSubset = import("zod").z.infer<
  typeof accessTicketSchema
> extends WithoutPurchase<AccessRead>
  ? true
  : never;
type _AccessWireSchemaSuperset = WithoutPurchase<AccessRead> extends import("zod").z.infer<
  typeof accessTicketSchema
>
  ? true
  : never;
const _accessWireSchemaDriftCheck: [
  _AccessWireSchemaSubset,
  _AccessWireSchemaSuperset,
] = [true, true];
void _accessWireSchemaDriftCheck;

type _AccessesWireSchemaSubset = import("zod").z.infer<
  typeof accessTicketsListSchema
> extends Accesses
  ? true
  : never;
type _AccessesWireSchemaSuperset = Accesses extends import("zod").z.infer<
  typeof accessTicketsListSchema
>
  ? true
  : never;
const _accessesWireSchemaDriftCheck: [
  _AccessesWireSchemaSubset,
  _AccessesWireSchemaSuperset,
] = [true, true];
void _accessesWireSchemaDriftCheck;

/**
 * Adds an access to the cache of accesses list
 */
const updateAccessesCache = (data: DataSession, access: Access) => {
  const { queryClient } = data;
  const qKeyAll = getQueryKey(trpc.accesses.listAll, undefined, "query");

  const allData = queryClient.getQueryData<Accesses>(qKeyAll);

  if (allData) {
    queryClient.setQueryData(
      qKeyAll,
      allData.map((x) => {
        if (x.id === access.id) {
          return {
            ...access,
          };
        }
        return x;
      }),
    );
  }
};

/**
 * Single-access query key. Bare tRPC key — no uid prefix. The SQLite
 * cache belongs to the data session and is cleared on account changes.
 */
export const accessesGetByIdQueryKey = (accessId: string) =>
  getQueryKey(trpc.accesses.getById, { accessId }, "query");

export const useAccessesGetByIdQuery = (data: DataSession, accessId: string) =>
  data.cache.durableQueryOptions({
    fallbackOnError: isAccessNetworkError,
    queryKey: accessesGetByIdQueryKey(accessId),
    queryFn: async () => {
      if (!accessId) {
        throw new InvalidOptionsError("useAccessesGetByIdQuery");
      }

      console.log(
        `useAccess :: queryFn called for accessId: ${accessId}, Platform: ${Platform.OS}`,
      );

      // Wait for auth to be ready before making the API call.
      // This ensures we have the correct uid (or null for public users)
      // and prevents race conditions where Firebase auth hasn't initialized yet
      // (e.g. cold start from a deep link where the query fires before auth).
      await waitForAuthReady(data.auth);

      return data.api.accesses.getById.query({ accessId });
    },
    schema: getAccessByIdOutputSchema as unknown as import("zod").z.ZodType<AccessRead>,
  });

/**
 * The signed-in buyer's own read of an access's purchase, never cached:
 * the purchase sheet's (`PurchaseTransport.read`), with the buyer's saved
 * answers in its fields, for the country their address names once it
 * names one (`deliverTo`). Null where the access offers no listing.
 */
export async function readAccessPurchase(
  data: DataSession,
  accessId: string,
  at?: { deliverTo?: string },
): Promise<Purchase | null> {
  await waitForAuthReady(data.auth);
  const access = getAccessByIdOutputSchema.parse(
    await data.api.accesses.getById.query({
      accessId,
      ...(at?.deliverTo ? { deliverTo: at.deliverTo } : {}),
    }),
  );
  return access.type === "music" ? null : access.purchase;
}

/**
 * Get a single access
 */
const useAccess = (accessId: string | undefined, refetchInterval?: number) => {
  const data = useData();
  const query = useQuery({
    ...useAccessesGetByIdQuery(data, accessId || ""),
    refetchInterval: refetchInterval || false,
    refetchOnMount: "always",
    enabled: !!accessId?.length,
  });

  useEffect(() => {
    if (query.data) {
      updateAccessesCache(data, query.data);
    }
  }, [data, query.data]);

  return query;
};

/**
 * List-all-accesses query key. Bare tRPC key — no uid prefix.
 */
export const accessesListAllQueryKey = () =>
  getQueryKey(trpc.accesses.listAll, undefined, "query");

export const accessesListAllQuery = (data: DataSession) =>
  data.cache.durableQueryOptions({
    fallbackOnError: isAccessNetworkError,
    queryKey: accessesListAllQueryKey(),
    queryFn: () => data.api.accesses.listAll.query(),
    schema:
      accessTicketsListSchema as unknown as import("zod").z.ZodType<Accesses>,
  });

/**
 * Get a list of accesses
 */
const useAccesses = () => {
  const data = useData();
  const query = useQuery({
    ...accessesListAllQuery(data),
  });

  return query;
};

type AllAccessesPaginatedPageParam =
  | {
      eventOffset: number | undefined;
      nonEventOffset: number | undefined;
    }
  | undefined;

type AllAccessesPaginatedOutput = RouterOutputs["accesses"]["listAllPaginated"];

const DEFAULT_ALL_ACCESSES_LIMIT = 15;

/**
 * Wire-shape schema for one page of `accesses.listAllPaginated`.
 *
 * One-way drift check (server → schema). Reverse direction would need
 * `.passthrough()`-equivalent index signature; unnecessary because every
 * property is enumerated.
 */
const allAccessesPaginatedPageSchema = z.object({
  event: z.object({
    data: z.array(accessTicketSchema),
    nextOffset: z.number().optional(),
  }),
  nonEvent: z.object({
    data: z.array(accessTicketSchema),
    nextOffset: z.number().optional(),
  }),
});

type _AllAccessesPaginatedPageWireSchemaCheck =
  AllAccessesPaginatedOutput extends z.infer<
    typeof allAccessesPaginatedPageSchema
  >
    ? true
    : never;
const _allAccessesPaginatedPageWireSchemaDriftCheck: _AllAccessesPaginatedPageWireSchemaCheck =
  true;
void _allAccessesPaginatedPageWireSchemaDriftCheck;

const allAccessesPaginatedPageParamSchema = z
  .object({
    eventOffset: z.number().optional(),
    nonEventOffset: z.number().optional(),
  })
  .optional();

const allAccessesPaginatedInfiniteSchema = infiniteDataSchema(
  allAccessesPaginatedPageSchema,
  allAccessesPaginatedPageParamSchema,
);

export const allAccessesPaginatedQuery = (data: DataSession) => ({
  queryKey: getQueryKey(trpc.accesses.listAllPaginated),
  queryFn: ({ pageParam }: { pageParam: AllAccessesPaginatedPageParam }) => {
    return data.api.accesses.listAllPaginated.query({
      event: {
        offset: pageParam?.eventOffset ?? 0,
        limit: DEFAULT_ALL_ACCESSES_LIMIT,
      },
      nonEvent: {
        offset: pageParam?.nonEventOffset ?? 0,
        limit: DEFAULT_ALL_ACCESSES_LIMIT,
      },
    });
  },
  getNextPageParam: (lastPage: AllAccessesPaginatedOutput) => {
    if (!lastPage?.event?.nextOffset && !lastPage?.nonEvent?.nextOffset) {
      return undefined;
    }
    return {
      eventOffset: lastPage?.event?.nextOffset,
      nonEventOffset: lastPage?.nonEvent?.nextOffset,
    };
  },
  initialPageParam: {
    eventOffset: 0,
    nonEventOffset: 0,
  },
  select: (response: InfiniteData<AllAccessesPaginatedOutput>) => {
    const data: Access[] = [];
    for (const page of response.pages ?? []) {
      if (!page) continue;
      const events = page.event?.data ?? [];
      const nonEvents = page.nonEvent?.data ?? [];
      const maxLength = Math.max(events.length, nonEvents.length);
      for (let i = 0; i < maxLength; i++) {
        if (i < events.length) data.push(events[i] as Access);
        if (i < nonEvents.length) data.push(nonEvents[i] as Access);
      }
    }
    return data;
  },
});

const useAllAccessesPaginated = () => {
  const data = useData();
  const queryKey = getQueryKey(trpc.accesses.listAllPaginated);

  // Cast through `any` to reconcile the typed `pageParam` queryFn with
  // react-query's `pageParam: unknown` constraint when `meta` + `initialData`
  // are present — same pattern as `useArtistStoreAccesses`. The factory
  // shape is preserved so callers can still spread it.
  const queryOptions = {
    ...allAccessesPaginatedQuery(data),
    // No "always" refetch (2026-08-03): the explore tab re-mounted this on
    // every navigation, refiring the fetch each time — invisible while the
    // legacy list was deterministic, visibly reshuffling under the engine.
    // The global 5-min staleTime governs freshness; gcTime matches so quick
    // back-and-forth serves cache.
    gcTime: 1000 * 60 * 5,
    ...data.cache.durableInfiniteOptions<AllAccessesPaginatedOutput>(
      queryKey,
      allAccessesPaginatedInfiniteSchema,
      {
        // Pre-warm `cached_accesses` from feed pages so subsequent
        // `useAccess(id)` reads resolve from the per-entity cache.
        decompose: (data: InfiniteData<AllAccessesPaginatedOutput>) =>
          data.pages.flatMap((p) => [
            ...(p?.event?.data ?? []),
            ...(p?.nonEvent?.data ?? []),
          ]),
        decomposeTable: cachedAccesses,
        decomposeIdField: "id",
        decomposeSchema: accessTicketSchema,
      },
    ),
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const query = useInfiniteQuery(queryOptions as any) as ReturnType<
    typeof useInfiniteQuery<AllAccessesPaginatedOutput, Error, Access[]>
  >;

  return query;
};

/**
 * Get a list of user accesses tickets — the requesting user's purchased
 * tickets. Auth-required: queryFn throws NoAuthError when no firebase user.
 * It first waits for Firebase to restore the session, like `getById`: on a
 * page reload the Wallet asked before the user was back, failed NoAuthError
 * and stayed empty until it was mounted again (seen 1 Oct on the web app).
 *
 * Bare tRPC key — no uid prefix. The session owns the cache across
 * account changes.
 */
export const accessTicketsQueryKey = () =>
  getQueryKey(trpc.user.accesses.listUserAccesses, undefined);

export const accessTicketsQuery = (data: DataSession) =>
  data.cache.durableQueryOptions({
    fallbackOnError: isAccessNetworkError,
    queryKey: accessTicketsQueryKey(),
    queryFn: async () => {
      await waitForAuthReady(data.auth);
      if (!data.auth.currentUser()) {
        throw new NoAuthError("accessTicketsQuery queryFn");
      }
      return data.api.user.accesses.listUserAccesses.query();
    },
    schema:
      accessTicketsListSchema as unknown as import("zod").z.ZodType<Accesses>,
  });

const useAccessTickets = () => {
  const data = useData();
  const query = useQuery({
    ...accessTicketsQuery(data),
    refetchOnMount: "always",
  });

  return query;
};

type ArtistAccessesParams =
  RouterInputs["catalog"]["artists"]["listCurrentAccessesByArtistId"];

type PageParam = {
  eventOffset: number;
  musicOffset: number;
  /** The artist's merch: a bucket of its own, asked for by this build. */
  merchOffset: number;
  /** Once a type is exhausted we send limit=0 to stop refetching the same rows. */
  eventDone: boolean;
  musicDone: boolean;
  merchDone: boolean;
};

type ArtistAcccessesOutput =
  RouterOutputs["catalog"]["artists"]["listCurrentAccessesByArtistId"];

const artistAccessesPageSchema = z.object({
  event: z.object({
    data: z.array(accessTicketSchema),
    nextOffset: z.number().optional(),
  }),
  music: z.object({
    data: z.array(accessTicketSchema),
    nextOffset: z.number().optional(),
  }),
  // Absent from pages stored before merch was asked for.
  merch: z
    .object({
      data: z.array(accessTicketSchema),
      nextOffset: z.number().optional(),
    })
    .optional(),
});

type _ArtistAccessesPageWireSchemaCheck =
  ArtistAcccessesOutput extends z.infer<typeof artistAccessesPageSchema>
    ? true
    : never;
const _artistAccessesPageWireSchemaDriftCheck: _ArtistAccessesPageWireSchemaCheck =
  true;
void _artistAccessesPageWireSchemaDriftCheck;

const artistAccessesPageParamSchema = z.object({
  eventOffset: z.number(),
  musicOffset: z.number(),
  eventDone: z.boolean(),
  musicDone: z.boolean(),
  // A page stored before merch was asked for: its merch from the start.
  // A refetch reads the first page's param and works out the rest.
  merchOffset: z.number().default(0),
  merchDone: z.boolean().default(false),
});

const artistAccessesInfiniteSchema = infiniteDataSchema(
  artistAccessesPageSchema,
  artistAccessesPageParamSchema,
);

export const artistAccessesQuery = (
  data: DataSession,
  params: ArtistAccessesParams,
) => ({
  queryKey: getQueryKey(
    trpc.catalog.artists.listCurrentAccessesByArtistId,
    params,
  ),
  queryFn: ({ pageParam }: { pageParam: PageParam }) => {
    if (!params.artistIds) {
      throw new InvalidOptionsError(
        "artistAccessesQuery:: artistIds is required",
      );
    }

    return data.api.catalog.artists.listCurrentAccessesByArtistId.query({
      artistIds: params.artistIds,
      event: {
        offset: pageParam.eventOffset,
        limit: pageParam.eventDone ? 0 : params.event?.limit || 3,
      },
      music: {
        offset: pageParam.musicOffset,
        limit: pageParam.musicDone ? 0 : params.music?.limit || 3,
      },
      merch: {
        offset: pageParam.merchOffset,
        limit: pageParam.merchDone ? 0 : params.merch?.limit || 3,
      },
    });
  },
  getNextPageParam: (
    lastPage: ArtistAcccessesOutput,
    _allPages: ArtistAcccessesOutput[],
    lastPageParam: PageParam,
  ): PageParam | undefined => {
    // Once a type is exhausted, lastPage.<type>.nextOffset is undefined. We
    // latch the `done` flag so we keep passing limit=0 instead of falling back
    // to offset=0 (which would refetch the first page and cause duplicates).
    const eventDone =
      lastPageParam.eventDone || lastPage?.event?.nextOffset === undefined;
    const musicDone =
      lastPageParam.musicDone || lastPage?.music?.nextOffset === undefined;
    const merchDone =
      lastPageParam.merchDone || lastPage?.merch?.nextOffset === undefined;

    if (eventDone && musicDone && merchDone) return undefined;

    return {
      eventOffset: lastPage?.event?.nextOffset ?? lastPageParam.eventOffset,
      musicOffset: lastPage?.music?.nextOffset ?? lastPageParam.musicOffset,
      merchOffset: lastPage?.merch?.nextOffset ?? lastPageParam.merchOffset,
      eventDone,
      musicDone,
      merchDone,
    };
  },
  initialPageParam: {
    eventOffset: 0,
    musicOffset: 0,
    merchOffset: 0,
    eventDone: false,
    musicDone: false,
    merchDone: false,
  } satisfies PageParam,
  select: (response: InfiniteData<ArtistAcccessesOutput>) => {
    const data: Access[] = [];

    for (const page of response.pages ?? []) {
      if (!page || !page.music || !page.event) {
        continue;
      }

      const music = page.music?.data ?? [];
      const event = page.event?.data ?? [];
      const merch = page.merch?.data ?? [];

      const maxLength = Math.max(music.length, event.length, merch.length);

      for (let i = 0; i < maxLength; i++) {
        if (i < event.length) {
          data.push(event[i]);
        }

        if (i < music.length) {
          data.push(music[i]);
        }

        if (i < merch.length) {
          data.push(merch[i]);
        }
      }
    }

    return data;
  },
});

/**
 * Get a list of accesses for an artist, and update the caches so we don't need to fetch again if you open the same access from elsewhere
 */
const useArtistAccesses = (
  artistIds: string[],
  options?: { refetchInterval?: number; enabled?: boolean },
) => {
  const data = useData();
  const queryKey = getQueryKey(
    trpc.catalog.artists.listCurrentAccessesByArtistId,
    { artistIds } as ArtistAccessesParams,
  );

  const queryOptions = {
    ...artistAccessesQuery(data, { artistIds } as ArtistAccessesParams),
    refetchInterval: options?.refetchInterval || false,
    refetchOnMount: "always" as const,
    enabled: options?.enabled ?? true,
    ...data.cache.durableInfiniteOptions<ArtistAcccessesOutput>(
      queryKey,
      artistAccessesInfiniteSchema,
      {
        decompose: (data: InfiniteData<ArtistAcccessesOutput>) =>
          data.pages.flatMap((p) => [
            ...(p?.event?.data ?? []),
            ...(p?.music?.data ?? []),
            ...(p?.merch?.data ?? []),
          ]),
        decomposeTable: cachedAccesses,
        decomposeIdField: "id",
        decomposeSchema: accessTicketSchema,
      },
    ),
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const query = useInfiniteQuery(queryOptions as any) as ReturnType<
    typeof useInfiniteQuery<ArtistAcccessesOutput, Error, Access[]>
  >;

  return query;
};

/**
 * Get a list of pinned accesses
 */
type AccessesPinnedOutput = RouterOutputs["accesses"]["listPinned"];

export const accessPinnedQueryKey = () =>
  getQueryKey(trpc.accesses.listPinned, undefined);

const accessPinnedQuery = (data: DataSession) =>
  data.cache.durableQueryOptions({
    fallbackOnError: isAccessNetworkError,
    queryKey: accessPinnedQueryKey(),
    queryFn: () => data.api.accesses.listPinned.query(),
    schema:
      accessTicketsListSchema as unknown as import("zod").z.ZodType<AccessesPinnedOutput>,
  });

/**
 * Similar accesses for a detail-page rail — the engine's access-more-like-this
 * surface (the anchor's vector is the query). Not durable-cached: the rail is
 * decorative and the list is anchor-specific; a fresh fetch per view is fine.
 */
const useSimilarAccesses = (accessId: string) => {
  const data = useData();
  const query = useQuery({
    queryKey: getQueryKey(trpc.accesses.listSimilar, { accessId }, "query"),
    queryFn: () => data.api.accesses.listSimilar.query({ accessId }),
  });

  return query;
};

const useAccessesPinned = () => {
  const data = useData();
  const query = useQuery({
    ...accessPinnedQuery(data),
    // No "always" refetch (2026-08-03): see useAllAccessesPaginated — every
    // explore-tab navigation refired the engine hero serve. 5-min global
    // staleTime governs freshness.
  });

  return query;
};

export {
  Access,
  AccessRead,
  useAccess,
  useAccesses,
  useAllAccessesPaginated,
  useAccessTickets,
  useArtistAccesses,
  updateAccessesCache,
  useAccessesPinned,
  useSimilarAccesses,
};
